// Centralized Type Definitions

import axios from 'axios';

// Manually define Axios types
export interface AxiosError extends Error {
  isAxiosError: boolean;
  response?: {
    status: number;
    data: any;
  };
  request?: any;
  config?: any;
  code?: string;
}

export interface AxiosResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: any;
  config: AxiosRequestConfig;
  request?: any;
}

export interface AxiosRequestConfig {
  url?: string;
  method?: string;
  baseURL?: string;
  headers?: any;
  params?: any;
  data?: any;
  timeout?: number;
  withCredentials?: boolean;
}

export type CustomAxiosError = AxiosError;
export type CustomAxiosResponse = AxiosResponse;
export type CustomAxiosRequestConfig = AxiosRequestConfig;

// Robust Axios export
export { axios };

// Utility function for promise type conversion
export function fixPromiseTypeAxios<T>(promise: Promise<T>): Promise<T> {
  return promise;
}

// Helper function to convert Promise types
export function convertIPromiseToPromise<T>(promise: Promise<T> | any): Promise<T> {
  return Promise.resolve(promise);
}

// Authentication Types
export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
  MANAGER = 'manager',
  READONLY = 'readonly',
  GUEST = 'guest'
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  expires_at?: number;
}

export interface UserData {
  id: number;
  username: string;
  email: string;
}

export interface User extends UserData {
  role: UserRole;
}

/**
 * Interface de base pour les données tenant
 */
export interface TenantData {
  id: number | string;
  name: string;
  domain?: string;
  status?: string;
  created_at?: string;
  features?: string[] | Record<string, boolean>;
  [key: string]: any;
}

/**
 * Interface pour les adhésions tenant
 */
export interface TenantMembership {
  id: number | string;
  tenant_id: number | string;
  tenant?: TenantData;
  user_id: number | string;
  roles?: string[];
  permissions?: string[];
  status?: string;
  created_at?: string;
  [key: string]: any;
}

/**
 * Interface complète pour le profil utilisateur enrichi
 */
export interface UserProfile {
  id: number | string;
  email: string;
  username: string;
  role: UserRole;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  last_login?: Date | string;
  
  // Propriétés enrichies provenant du token JWT
  permissions?: string[];
  active_features?: string[];
  tenants?: TenantData[];
  tenant_memberships?: TenantMembership[];
  current_tenant?: TenantData;
  
  // Propriétés additionnelles pour la flexibilité
  [key: string]: any;
}

export interface AuthResponse {
  tokens: AuthTokens;
  user: UserProfile;
}

// Add missing authentication-related types
export type LoginResponse = {
  tokens: {
    access_token: string;
    refresh_token: string;
  };
  user: UserProfile;
};

export type TokenRefreshResponse = {
  access_token: string;
  refresh_token: string;
};

export type LoginCredentials = {
  email: string;
  password: string;
};

export type AuthUserRole = UserRole;

// Additional auth-related types from previous implementation
export const USER_ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  MANAGER: 'manager',
  READONLY: 'readonly'
};

export function validateUserRole(role: string): role is UserRole {
  return Object.values(USER_ROLES).includes(role);
}

// Django Rest Auth Login Response
export interface DjangoRestAuthLoginResponse {
  user: UserProfile;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

// Auth API
export interface AuthAPI {
  login(credentials: { 
    username: string; 
    password: string 
  }): Promise<UserProfile>;
  register(userData: { 
    email: string; 
    password: string; 
    username?: string; 
    first_name?: string; 
    last_name?: string; 
  }): Promise<UserProfile>;
  logout(): Promise<void>;
  
  // Private helper methods (for type completeness)
  getCsrfToken?(): string;
  isValidUserProfile?(user: any): user is UserProfile;
}

// Model Types
import type { ModelField } from '../types/models';

export interface ModelDefinition {
  id: string;
  name: string;
  description?: string;
  fields: ModelField[];
}

export interface ModelInstance {
  id: string;
  model: ModelDefinition;
  data: Record<string, unknown>;
}

export interface ModelAdminResponse {
  models: ModelDefinition[];
  total: number;
}

export const modelDefinitions: ModelDefinition[] = [];

// System Settings
export interface SystemSettings {
  theme: 'light' | 'dark';
  language: string;
  notifications: boolean;
  api_timeout: number;
  max_asset_scan_duration: number;
  log_retention_days: number;
  enable_two_factor_auth: boolean;
  debug_mode: boolean;
  maintenance_mode: boolean;
  app_name: string;
  environment: 'development' | 'staging' | 'production';
  max_scan_duration: number;
  default_scan_interval: number;
  notification_email: string;
  log_level: 'debug' | 'info' | 'warn' | 'error';
  timezone: string;
  // Add other system-wide settings as needed
}

// Mock API Type
export interface MockApiConfig {
  enabled: boolean;
  forceRealApi: boolean;
}

export const mockApi: MockApiConfig = {
  enabled: false,
  forceRealApi: false
};
